import gql from 'graphql-tag';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Stack from '@mui/material/Stack';

import WarningIcon from '@watershed/icons/components/Warning';
import Callout from '@watershed/shared-frontend/components/Callout';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import { Dialog } from '@watershed/ui-core/components/Dialog';
import { AutocompleteFieldNonFormik } from '@watershed/ui-core/components/Form/AutocompleteField';
import {
  TextFieldMultilineNonFormik,
  TextFieldNonFormik,
} from '@watershed/ui-core/components/Form/TextField';
import ProgressButton from '@watershed/ui-core/components/ProgressButton';

import { GQUpdateOauthClientInput } from '../../../generated/graphql-schema-types';
import {
  useAllRolesQuery,
  useUpdateApiOAuthClientMutation,
} from '../../../generated/urql';
import ErrorPage from '../../ErrorPage';
import {
  getDefaultDurationMinutes,
  MAX_API_KEY_EXPIRY_MINUTES,
  MIN_API_KEY_EXPIRY_MINUTES,
  minutesToMs,
  msToMinutes,
} from '../api/oauthClientDurationUtils';

gql`
  mutation UpdateApiOAuthClient($input: UpdateOauthClientInput!)
  @withOwner(owner: EnterpriseFoundations) {
    updateOauthClient(input: $input) {
      client {
        id
        name
        description
        apiKeyExpiryMs
        roles {
          id
          name
        }
      }
    }
  }
`;

type ApiOAuthClientData = {
  id: string;
  name: string;
  description?: string | null;
  apiKeyExpiryMs: number;
  roles: Array<{ id: string; name: string }>;
};

export default function UpdateApiOAuthClientDialog({
  open,
  onClose,
  client,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  client: ApiOAuthClientData | null;
  onSuccess?: () => void;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [updateInput, setUpdateInput] = useState<GQUpdateOauthClientInput>({
    id: '',
    name: '',
    description: '',
    roleIds: [],
    apiKeyExpiryMs: minutesToMs(getDefaultDurationMinutes()),
  });
  const [originalRoleIds, setOriginalRoleIds] = useState<Array<string>>([]);
  const [originalApiKeyExpiryMs, setOriginalApiKeyExpiryMs] = useState<number>(
    minutesToMs(getDefaultDurationMinutes())
  );
  const [durationMinutes, setDurationMinutes] = useState<string>(
    getDefaultDurationMinutes().toString()
  );
  const [, executeUpdate] = useUpdateApiOAuthClientMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Update form when client changes
  useEffect(() => {
    if (client) {
      const roleIds = client.roles.map((role) => role.id);
      const clientDurationMinutes = msToMinutes(client.apiKeyExpiryMs);
      setUpdateInput({
        id: client.id,
        name: client.name,
        description: client.description || '',
        roleIds,
        apiKeyExpiryMs: client.apiKeyExpiryMs,
      });
      setOriginalRoleIds(roleIds);
      setOriginalApiKeyExpiryMs(client.apiKeyExpiryMs);
      setDurationMinutes(clientDurationMinutes.toString());
    }
  }, [client]);

  // Check if roles have been modified
  const rolesHaveChanged = useMemo(() => {
    if (!client) return false;

    const currentRoleIds = [...(updateInput.roleIds || [])].sort();
    const originalSorted = [...originalRoleIds].sort();

    // If no roles are selected, they can't submit the update form
    // so let's not show the warning.
    if (currentRoleIds.length === 0) {
      return false;
    }

    return (
      currentRoleIds.length !== originalSorted.length ||
      currentRoleIds.some((id, index) => id !== originalSorted[index])
    );
  }, [client, originalRoleIds, updateInput.roleIds]);

  // Check if API key expiry has been modified
  const apiKeyExpiryHasChanged = useMemo(() => {
    if (!client) return false;
    return updateInput.apiKeyExpiryMs !== originalApiKeyExpiryMs;
  }, [client, originalApiKeyExpiryMs, updateInput.apiKeyExpiryMs]);

  const resetUpdateInput = () => {
    setUpdateInput({
      id: '',
      name: '',
      description: '',
      roleIds: [],
      apiKeyExpiryMs: minutesToMs(getDefaultDurationMinutes()),
    });
    setOriginalRoleIds([]);
    setOriginalApiKeyExpiryMs(minutesToMs(getDefaultDurationMinutes()));
    setDurationMinutes(getDefaultDurationMinutes().toString());
  };

  const isDurationValid = () => {
    const minutes = parseInt(durationMinutes, 10);
    return (
      !isNaN(minutes) &&
      minutes >= MIN_API_KEY_EXPIRY_MINUTES &&
      minutes <= MAX_API_KEY_EXPIRY_MINUTES
    );
  };

  const isSubmitDisabled =
    updateInput.name === '' ||
    !updateInput.roleIds ||
    updateInput.roleIds.length === 0 ||
    !isDurationValid();

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      resetUpdateInput();
    }, 500);
  };

  const onSubmit = async () => {
    if (!client) return;

    setSubmitting(true);
    const result = await executeUpdate({
      input: updateInput,
    });

    if (result.error) {
      enqueueSnackbar(
        t({
          message: 'Error updating OAuth credentials.',
          context: 'Error message for OAuth client update failure',
        }),
        {
          variant: 'error',
        }
      );
      setSubmitting(false);
      return;
    }

    enqueueSnackbar(
      t({
        message: 'OAuth credentials updated successfully.',
        context: 'Success message for OAuth client update',
      }),
      {
        variant: 'success',
      }
    );

    setSubmitting(false);
    onSuccess?.();
    handleClose();
  };

  const [rolesResult] = useAllRolesQuery();

  if (rolesResult.error) {
    return <ErrorPage error={rolesResult.error} />;
  }

  const roles =
    rolesResult.data?.allRoles.filter((role) => role.workosGroupId === null) ??
    []; // don't allow api keys to be assigned to SCIM roles

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      header={{
        title: t({
          message: 'Update OAuth credentials',
          context: 'Dialog title for updating OAuth client',
        }),
      }}
      actions={
        <ProgressButton
          onClick={onSubmit}
          isInProgress={submitting}
          disabled={isSubmitDisabled}
          tooltip={
            isSubmitDisabled
              ? t({
                  message: 'Please fill out all required fields',
                  context: 'form tooltip',
                })
              : undefined
          }
        >
          <Trans context="Button copy for updating OAuth credentials">
            Update OAuth credentials
          </Trans>
        </ProgressButton>
      }
    >
      <Stack gap={3}>
        <TextFieldNonFormik
          id="name"
          label={t({ message: 'Name', context: 'form field label' })}
          required
          value={updateInput.name || ''}
          onChange={(e) =>
            setUpdateInput({ ...updateInput, name: e.target.value })
          }
        />
        <TextFieldMultilineNonFormik
          rows={2}
          id="description"
          label={t({ message: 'Description', context: 'form field label' })}
          placeholder={t({
            message: 'Add a note…',
            context: 'multiline form field placeholder',
          })}
          value={updateInput.description || ''}
          onChange={(e) =>
            setUpdateInput({ ...updateInput, description: e.target.value })
          }
          required={false}
        />
        <AutocompleteFieldNonFormik
          label={t({ message: 'Roles', context: 'form field label' })}
          sublabel={t({
            message:
              'These credentials will inherit the permissions associated with the role(s) selected.',
            context: 'form field sublabel',
          })}
          id="roleIds"
          options={roles.map((r: { id: string }) => r.id)}
          getOptionLabel={(roleId) =>
            roles.find((r: { id: string }) => r.id === roleId)?.name ?? ''
          }
          multiple
          required
          value={updateInput.roleIds || []}
          onChange={(_, value) =>
            setUpdateInput({
              ...updateInput,
              roleIds: value.map((v) =>
                typeof v === 'string' ? v : (v as { value: string }).value
              ) as Array<string>,
            })
          }
        />
        <TextFieldNonFormik
          label={t({
            message: 'Token expiration (minutes)',
            context: 'form field label',
          })}
          sublabel={t({
            message: `Tokens will expire after this duration. Min: ${MIN_API_KEY_EXPIRY_MINUTES}, Max: ${MAX_API_KEY_EXPIRY_MINUTES} (1 day)`,
            context:
              'form field sublabel, min/max refer to minimum and maximum',
          })}
          id="apiKeyExpiryMinutes"
          type="number"
          step={1}
          min={MIN_API_KEY_EXPIRY_MINUTES}
          max={MAX_API_KEY_EXPIRY_MINUTES}
          value={durationMinutes}
          validationState={isDurationValid() ? 'default' : 'error'}
          validationMessage={
            isDurationValid()
              ? undefined
              : t({
                  message: 'Please enter a valid duration',
                  context: 'form field validation message',
                })
          }
          onChange={(e) => {
            const value = e.target.value;

            // Prevent decimal input - only allow integers
            if (value !== '' && (value.includes('.') || value.includes(','))) {
              return;
            }

            setDurationMinutes(value);

            const minutes = parseInt(value, 10);
            if (!isNaN(minutes) && minutes > 0) {
              setUpdateInput({
                ...updateInput,
                apiKeyExpiryMs: minutesToMs(minutes),
              });
            }
          }}
          required
        />
        {(rolesHaveChanged || apiKeyExpiryHasChanged) && (
          <Callout
            variant="warning"
            IconComponent={WarningIcon}
            title={t({
              message: 'Security-sensitive changes detected',
              context: 'Warning callout title for OAuth security changes',
            })}
            description={t({
              message:
                'Any existing tokens for this OAuth client will be revoked when you save these changes.',
              context: 'Warning callout description for OAuth security changes',
            })}
          />
        )}
      </Stack>
    </Dialog>
  );
}
